import { useState } from "react";
import { MapPin, Clock, ArrowUpRight } from "lucide-react";
import { SHOWROOMS, mapsEmbedUrl } from "@/data/showrooms";

function Frame({ title, note, children }) {
  return (
    <section className="mb-24">
      <div className="mb-6">
        <h2 className="font-serif text-3xl font-light tracking-tight">{title}</h2>
        <p className="mt-2 text-sm font-light text-muted-foreground max-w-xl">{note}</p>
      </div>
      <div className="border border-foreground/20 overflow-hidden">{children}</div>
    </section>
  );
}

function MapFrame({ showroom, className = "aspect-[4/3]" }) {
  return (
    <iframe
      title={`Map — ${showroom.name}`}
      data-testid={`map-embed-${showroom.id}`}
      src={mapsEmbedUrl(showroom)}
      className={`w-full border-0 ${className}`}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      allowFullScreen
    />
  );
}

const TwinMaps = () => (
  <div className="bg-background p-10 md:p-14">
    <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-3">Visit us</p>
    <h3 className="font-serif text-3xl font-light tracking-tight mb-10">
      Two showrooms, <span className="italic">one obsession.</span>
    </h3>
    <div className="grid md:grid-cols-2 gap-6">
      {SHOWROOMS.map((s) => (
        <div key={s.id}>
          <MapFrame showroom={s} />
          <div className="border border-t-0 border-border p-6">
            <p className="text-[10px] tracking-[0.25em] uppercase text-secondary-foreground mb-2">{s.label}</p>
            <p className="font-serif text-xl font-light tracking-tight">{s.name}</p>
            <p className="mt-2 text-sm font-light text-muted-foreground leading-relaxed">{s.address}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SwitcherMap = () => {
  const [active, setActive] = useState(SHOWROOMS[0]);
  return (
    <div className="bg-background p-10 md:p-14">
      <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-3">Visit us</p>
      <h3 className="font-serif text-3xl font-light tracking-tight mb-10">
        Find your <span className="italic">nearest</span> showroom.
      </h3>
      <div className="flex flex-wrap gap-3 mb-6">
        {SHOWROOMS.map((s) => (
          <button
            key={s.id}
            data-testid={`map-tab-${s.id}`}
            onClick={() => setActive(s)}
            className={`text-xs tracking-[0.18em] uppercase px-5 py-3 border transition-colors duration-300 ${
              active.id === s.id
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-secondary-foreground hover:border-foreground"
            }`}
          >
            {s.label} — {s.name}
          </button>
        ))}
      </div>
      <MapFrame showroom={active} className="h-[380px] md:h-[440px]" />
      <div className="border border-t-0 border-border p-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm font-light text-muted-foreground flex items-start gap-2.5 max-w-md">
          <MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {active.address}
        </p>
        <a
          href={active.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="map-directions-link"
          className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase"
        >
          <span className="border-b border-foreground/40 pb-1 transition-colors duration-300 group-hover:border-foreground">
            Get directions
          </span>
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </div>
  );
};

const SplitMap = () => {
  const [active, setActive] = useState(SHOWROOMS[0]);
  return (
    <div className="bg-background grid lg:grid-cols-2">
      <div className="min-h-[320px] lg:min-h-[480px]">
        <MapFrame showroom={active} className="h-full min-h-[320px] lg:min-h-[480px]" />
      </div>
      <div className="p-10 md:p-14 flex flex-col justify-center">
        <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-3">Visit us</p>
        <h3 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-8">
          Walk the <span className="italic">yard.</span>
        </h3>
        <div className="flex gap-3 mb-8">
          {SHOWROOMS.map((s) => (
            <button
              key={s.id}
              data-testid={`split-tab-${s.id}`}
              onClick={() => setActive(s)}
              className={`text-[10px] tracking-[0.18em] uppercase px-4 py-2.5 border transition-colors duration-300 ${
                active.id === s.id
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-secondary-foreground hover:border-foreground"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <p className="font-serif text-2xl font-light tracking-tight mb-3">{active.name}</p>
        <p className="text-sm font-light text-muted-foreground leading-relaxed flex items-start gap-2.5">
          <MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {active.address}
        </p>
        <p className="mt-3 text-sm font-light text-muted-foreground flex items-center gap-2.5">
          <Clock className="w-4 h-4 shrink-0" /> Open daily till 7:00 PM
        </p>
        <a
          href={active.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="split-directions-link"
          className="group mt-8 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase"
        >
          <span className="border-b border-foreground/40 pb-1 transition-colors duration-300 group-hover:border-foreground">
            Get directions
          </span>
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </div>
  );
};

export default function MapOptions() {
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 pt-40 pb-32" data-testid="map-options-page">
      <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">
        Preview — not linked in the menu
      </p>
      <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tighter leading-[0.98] mb-6">
        Maps, <span className="italic">three ways.</span>
      </h1>
      <p className="max-w-lg text-base font-light leading-relaxed text-muted-foreground mb-20">
        Live Google Maps embeds for both showrooms. Pick a layout — any of them can live on the
        Contact page or the Home page.
      </p>

      <Frame
        title="Option 1 — Twin maps"
        note="Both showrooms side by side, each with its own interactive map. Visitors compare locations at a glance."
      >
        <TwinMaps />
      </Frame>

      <Frame
        title="Option 2 — One map, showroom switcher"
        note="A single large map with tabs to flip between showrooms. Cleaner, less scrolling."
      >
        <SwitcherMap />
      </Frame>

      <Frame
        title="Option 3 — Split editorial"
        note="Map on one side, showroom details on the other — the most premium, magazine-style layout."
      >
        <SplitMap />
      </Frame>
    </div>
  );
}
